import { useEffect, useState } from 'react';
import { Grid, InputLabel, OutlinedInput } from '@mui/material';
import PaymentsIcon from '@mui/icons-material/Payments';
import { FormDrawer, FormDrawerGroup } from '../../../../../components/form-drawer';
import { DateField, FieldError } from '../../../../../components/form-page-parts';
import BrazilianNumberInput from '../../../../../components/brazilian-number-input';
import { ILiquidationResponse } from '../../../../../model/sge/liquidation/response/liquidation-response';
import { IPaymentAddEditRequest } from '../../../../../model/sge/payment/request/payment-add-edit-request';
import { IPaymentResponse } from '../../../../../model/sge/payment/response/payment-response';
import { hasErrors } from '../../../../../utils/validation';
import { PaymentForm, projectTotal, toPaymentForm, validatePayment } from './amendment-detail-form-utils';
import { ProgressStat, ProgressSummary } from './progress-stat';

interface PaymentDrawerProps {
    open: boolean;
    initial?: IPaymentResponse;
    liquidation?: ILiquidationResponse;
    payments: IPaymentResponse[];
    isSaving: boolean;
    onClose: () => void;
    onSave: (value: IPaymentAddEditRequest) => void;
}

export function PaymentDrawer({ open, initial, liquidation, payments, isSaving, onClose, onSave }: PaymentDrawerProps) {
    const [form, setForm] = useState<PaymentForm>({});
    const [showValidationErrors, setShowValidationErrors] = useState(false);
    const isEdit = !!initial;
    const validation = validatePayment(form);
    const errors = showValidationErrors ? validation : {};
    const liquidatedAmount = liquidation?.liquidatedAmount || 0;
    const projectedPaid = projectTotal(payments, (p) => p.paidAmount, initial?.id, form.paidAmount);
    const exceedsLiquidation = !!liquidation && projectedPaid > liquidatedAmount;

    useEffect(() => {
        if (!open) return;
        setForm(toPaymentForm(initial));
        setShowValidationErrors(false);
    }, [open, initial]);

    function update(patch: Partial<PaymentForm>) {
        setForm((current) => ({ ...current, ...patch }));
    }

    function handleSave() {
        if (hasErrors(validation)) {
            setShowValidationErrors(true);
            return;
        }
        onSave(form as IPaymentAddEditRequest);
    }

    return (
        <FormDrawer
            open={open}
            icon={<PaymentsIcon />}
            title={isEdit ? 'Editar pagamento' : 'Novo pagamento'}
            subtitle={liquidation?.liquidationNumber ? `Liquidação Nº ${liquidation.liquidationNumber}` : undefined}
            saveLabel={isEdit ? 'Salvar alterações' : 'Adicionar pagamento'}
            isSaving={isSaving}
            onClose={onClose}
            onSave={handleSave}
        >
            {liquidation && (
                <ProgressSummary>
                    <ProgressStat label="Pago da liquidação" current={projectedPaid} total={liquidatedAmount} color="success" />
                </ProgressSummary>
            )}

            <FormDrawerGroup title="Pagamento">
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                        <InputLabel required>Nº da ordem de pagamento</InputLabel>
                        <OutlinedInput fullWidth autoFocus autoComplete="off" value={form.paymentOrderNumber || ''} onChange={(e) => update({ paymentOrderNumber: e.target.value })} error={!!errors.paymentOrderNumber} />
                        <FieldError message={errors.paymentOrderNumber} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <DateField label="Data" required value={form.paymentDate} onChange={(value) => update({ paymentDate: value || undefined })} error={errors.paymentDate} />
                    </Grid>
                    <Grid item xs={12}>
                        <InputLabel required>Valor pago</InputLabel>
                        <BrazilianNumberInput
                            fullWidth
                            value={form.paidAmount}
                            onChange={(paidAmount) => update({ paidAmount })}
                            decimals={2}
                            prefix="R$"
                            placeholder="0,00"
                            error={!!errors.paidAmount}
                        />
                        <FieldError message={errors.paidAmount || (exceedsLiquidation && 'O valor pago excede o valor da liquidação')} />
                    </Grid>
                </Grid>
            </FormDrawerGroup>
        </FormDrawer>
    );
}
